// SPDX-License-Identifier: MIT

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { resolveTheme, buildThemeCss } from './config';
import { COMPONENT_CSS } from './styles';
import {
  setChoice,
  setConsented,
  hasConsented,
  consentSource,
  clearAll,
  displayStates,
  appliedStates,
} from './consentStore';
import { applyConsent } from './consentRuntime';
import { resolveGeoDecision } from './geo';
import { DEFAULT_TEXT } from './defaults';
import { CONSENT_EVENT, setConsentState } from './consentState';

// Dispatched on window whenever consent is applied (on load and on every change),
// with detail { consent: { <categoryId>: boolean } }. Lets site-specific
// integrations react to consent — the useConsent hook / ConsentGate subscribe to
// it, and it's also how the GrowthBook provider is gated (see gatsby-browser.js).
export { CONSENT_EVENT };

const notifyConsent = (states) => {
  // Keep the shared in-memory state current (drives useConsent) even where
  // CustomEvent is unavailable, then notify subscribers.
  setConsentState(states);
  if (typeof window === 'undefined' || typeof window.CustomEvent !== 'function') return;
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: { consent: states } }));
};

// Opens the preferences modal from anywhere — call openPreferences() (e.g. from a
// footer "Cookie preferences" link) to let visitors change/withdraw consent even
// when the floating icon is hidden.
export const OPEN_EVENT = 'cookieconsentopen';

export const openPreferences = () => {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(OPEN_EVENT));
};

const CookieIcon = () => (
  <svg width="34" height="34" viewBox="0 0 38 38" aria-hidden="true">
    <path d="M19.1 1.2c-.1-.4-.4-.8-.8-.9C16.6.1 14.8.4 13.3 1.2L7.5 4.3C6 5.1 4.7 6.4 4 8L1.1 13.9c-.8 1.6-1 3.4-.7 5.1l1.1 6.5c.3 1.7 1.1 3.3 2.4 4.5l4.7 4.6c1.3 1.2 2.9 2 4.6 2.2l6.5.9c1.7.2 3.5-.1 5-.9l5.8-3.1c1.5-.8 2.8-2.1 3.5-3.7l2.9-5.9c.8-1.6 1-3.4.7-5.1-.1-.4-.4-.7-.8-.8-4-.6-7.3-3.7-8.2-7.6-.1-.6-.6-1-1.2-1.1-4.3-.7-7.6-4-8.3-8.3ZM15.3 10.3a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5ZM10.3 25.3a2.5 2.5 0 1 1 5 0 2.5 2.5 0 0 1-5 0Zm17.5-5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5Z" />
  </svg>
);

const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" aria-hidden="true">
    <path d="M19.4 3.4a2 2 0 1 0-2.8-2.8L10 7.2 3.4.6A2 2 0 0 0 .6 3.4L7.2 10 .6 16.6a2 2 0 1 0 2.8 2.8L10 12.8l6.6 6.6a2 2 0 0 0 2.8-2.8L12.8 10l6.6-6.6Z" />
  </svg>
);

const FOCUSABLE = 'button, a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Traps Tab focus within `ref` and calls onEscape on Escape, while active.
const useFocusTrap = (ref, active, onEscape) => {
  useEffect(() => {
    if (!active || !ref.current) return undefined;
    const container = ref.current;
    const focusable = () => Array.from(container.querySelectorAll(FOCUSABLE));
    const first = focusable()[0];
    if (first) first.focus();

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (onEscape) onEscape();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = focusable();
      if (items.length === 0) return;
      const firstEl = items[0];
      const lastEl = items[items.length - 1];
      if (e.shiftKey && document.activeElement === firstEl) {
        lastEl.focus();
        e.preventDefault();
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        firstEl.focus();
        e.preventDefault();
      }
    };
    container.addEventListener('keydown', onKeyDown);
    return () => container.removeEventListener('keydown', onKeyDown);
  }, [ref, active, onEscape]);
};

const ConsentManager = ({ config = {} }) => {
  const {
    namespace = 'cookieConsent',
    consentTypes = [],
    position = 'bottomRight',
    icon = {},
    backdrop = {},
    eventName,
    cookieDomain,
    cookieMaxAgeDays,
    geo,
  } = config;
  const cookieOptions = { domain: cookieDomain, maxAgeDays: cookieMaxAgeDays };
  const text = { ...DEFAULT_TEXT, ...(config.text || {}) };
  const iconPosition = icon.position || 'bottomLeft';
  // `icon.position: 'hide'` shows no floating re-open icon after a choice — the
  // banner just closes and nothing is left on the page. The resting state is then
  // `null` (renders nothing) instead of the icon.
  const restingView = iconPosition === 'hide' ? null : 'icon';
  const showBackdrop = backdrop.show !== false;

  const themeCss = useMemo(
    () => buildThemeCss(resolveTheme(config)) + COMPONENT_CSS,
    [config]
  );

  // 'banner' | 'modal' | 'icon' | null. null until mounted (SSR renders nothing).
  const [view, setView] = useState(null);
  const [draft, setDraft] = useState({});
  const appliedRef = useRef(null);
  const bannerRef = useRef(null);
  const modalRef = useRef(null);

  // Enforce stored consent on load, then decide the initial view. onLoad: true
  // loads granted trackers but never runs onReject (which could reload the page,
  // looping on every load).
  useEffect(() => {
    if (consentTypes.length === 0) return undefined;

    const enforceStored = () => {
      const applied = appliedStates(namespace, consentTypes);
      applyConsent(consentTypes, applied, null, { eventName, onLoad: true });
      notifyConsent(applied);
      appliedRef.current = applied;
    };
    enforceStored();

    // No geo targeting, or the visitor is in scope: prompt any visitor without a
    // stored choice, and keep a returning visitor's choice.
    if (!geo || resolveGeoDecision(geo)) {
      setView(hasConsented(namespace) ? restingView : 'banner');
      return undefined;
    }

    // Out of scope → imply consent (grant all, no banner). A deliberate choice
    // ('explicit') or an already-implied one is kept; a stored choice from before
    // geo targeting (no source) is replaced by implied consent.
    const source = consentSource(namespace);
    if (source === 'explicit' || source === 'implied') {
      setView(restingView);
      return undefined;
    }
    if (hasConsented(namespace)) clearAll(namespace, cookieOptions);
    commit(statesFor(true), 'implied');
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persists `states`, records that a choice was made (source 'explicit' for a
  // banner/preferences action, 'implied' when granted out of a geo zone), applies
  // side effects, and returns to the icon.
  const commit = useCallback(
    (states, source = 'explicit') => {
      consentTypes.forEach((type) => {
        if (!type.required) setChoice(namespace, type.id, states[type.id], cookieOptions);
      });
      setConsented(namespace, cookieOptions, source);
      applyConsent(consentTypes, states, appliedRef.current, { eventName });
      notifyConsent(states);
      appliedRef.current = states;
      setView(restingView);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [consentTypes, namespace, eventName, cookieDomain, cookieMaxAgeDays, restingView]
  );

  const statesFor = useCallback(
    (optionalValue) => {
      const states = {};
      consentTypes.forEach((type) => {
        states[type.id] = type.required ? true : optionalValue;
      });
      return states;
    },
    [consentTypes]
  );

  const acceptAll = useCallback(() => commit(statesFor(true)), [commit, statesFor]);
  const rejectAll = useCallback(() => commit(statesFor(false)), [commit, statesFor]);
  const savePreferences = useCallback(() => commit(draft), [commit, draft]);

  const openModal = useCallback(() => {
    setDraft(displayStates(namespace, consentTypes));
    setView('modal');
  }, [namespace, consentTypes]);

  const closeModal = useCallback(() => {
    setView(hasConsented(namespace) ? restingView : 'banner');
  }, [namespace, restingView]);

  // Let anything on the page re-open the preferences modal via openPreferences()
  // (e.g. a footer "Cookie preferences" link) — required so visitors can always
  // withdraw consent, even when the floating icon is hidden.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handler = () => openModal();
    window.addEventListener(OPEN_EVENT, handler);
    return () => window.removeEventListener(OPEN_EVENT, handler);
  }, [openModal]);

  useFocusTrap(modalRef, view === 'modal', closeModal);
  useFocusTrap(bannerRef, view === 'banner', undefined);

  if (view === null) return null;

  const backdropVisible = showBackdrop && (view === 'banner' || view === 'modal');

  return (
    <div id="cc-root">
      <style dangerouslySetInnerHTML={{ __html: themeCss }} />

      {backdropVisible && <div id="cc-backdrop" aria-hidden="true" />}

      {view === 'banner' && (
        <div
          id="cc-banner"
          className={`cc-panel cc-pos-${position}`}
          role="dialog"
          aria-modal="true"
          aria-label={text.modalTitle}
          ref={bannerRef}
        >
          <div dangerouslySetInnerHTML={{ __html: text.bannerDescription }} />
          <div className="cc-actions">
            <button type="button" className="cc-button" onClick={acceptAll}>
              {text.acceptAll}
            </button>
            <button type="button" className="cc-button" onClick={rejectAll}>
              {text.rejectNonEssential}
            </button>
            <button type="button" className="cc-button-link" onClick={openModal}>
              {text.preferences}
            </button>
          </div>
        </div>
      )}

      {view === 'modal' && (
        <div
          id="cc-modal"
          className="cc-panel cc-pos-center"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cc-modal-title"
          ref={modalRef}
        >
          <header>
            <h2 id="cc-modal-title">{text.modalTitle}</h2>
            <button
              type="button"
              className="cc-close"
              aria-label={text.close}
              onClick={closeModal}
            >
              <CloseIcon />
            </button>
          </header>
          <div dangerouslySetInnerHTML={{ __html: text.modalDescription }} />

          <div className="cc-types">
            {consentTypes.map((type) => (
              <fieldset key={type.id}>
                <legend>{type.label}</legend>
                <div className="cc-type-row">
                  <div dangerouslySetInnerHTML={{ __html: type.description }} />
                  <label className="cc-toggle" htmlFor={`cc-consent-${type.id}`}>
                    <input
                      id={`cc-consent-${type.id}`}
                      type="checkbox"
                      aria-label={type.label}
                      checked={type.required ? true : !!draft[type.id]}
                      disabled={type.required}
                      onChange={(e) =>
                        setDraft((prev) => ({ ...prev, [type.id]: e.target.checked }))
                      }
                    />
                    <span className="cc-track" aria-hidden="true" />
                    <span className="cc-thumb" aria-hidden="true" />
                    <span className="cc-toggle-off" aria-hidden="true">Off</span>
                    <span className="cc-toggle-on" aria-hidden="true">On</span>
                  </label>
                </div>
              </fieldset>
            ))}
          </div>

          <footer>
            <button type="button" className="cc-button" onClick={savePreferences}>
              {text.save}
            </button>
            <button type="button" className="cc-button" onClick={rejectAll}>
              {text.rejectNonEssential}
            </button>
          </footer>
        </div>
      )}

      {view === 'icon' && (
        <button
          id="cc-icon"
          className={`cc-pos-${iconPosition}`}
          type="button"
          aria-label={text.iconLabel}
          title={text.iconLabel}
          onClick={openModal}
        >
          <CookieIcon />
        </button>
      )}
    </div>
  );
};

export default ConsentManager;
